import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getMe, updateUser, setPassword, listSessions, revokeSession, listRefreshTokens, revokeRefreshToken, listTotpDevices, deleteTotpDevice } from '../../config/authentik-api';
import { Camera, Save, Eye, EyeOff, Shield, Monitor, Globe, Trash2, RefreshCw, AlertTriangle, WifiOff, AppWindow, MessageSquare, Laptop, Clock } from 'lucide-react';

/* ─── API availability hook ─── */

const useApiAvailable = () => {
  const [available, setAvailable] = useState(null); // null = checking, true/false = result
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    getMe()
      .then((data) => {
        setUserData(data.user || data);
        setAvailable(true);
      })
      .catch(() => {
        setAvailable(false);
      });
  }, []);

  return { available, userData };
};

const ApiUnavailableBanner = () => (
  <div className="flex items-start gap-3 p-4 rounded-xl mb-6" style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
    <WifiOff size={18} style={{ color: '#F97316', marginTop: '2px', flexShrink: 0 }} />
    <div>
      <p className="text-sm font-medium text-foreground-primary">Limited functionality</p>
      <p className="text-xs text-foreground-secondary mt-1">
        Unable to connect to the identity server. Some settings like password change, 2FA, and session management are temporarily unavailable. Your profile information is shown from your last login.
      </p>
    </div>
  </div>
);

const AccountSettings = () => {
  const { available, userData } = useApiAvailable();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground-primary">Account Settings</h1>
        <p className="text-sm text-foreground-secondary mt-1">Manage your profile, security, and active sessions</p>
      </div>
      {available === false && <ApiUnavailableBanner />}
      <ProfileSection apiAvailable={available} apiUserData={userData} />
      <PasswordSection apiAvailable={available} apiUserData={userData} />
      <TwoFactorSection apiAvailable={available} />
      <SessionsSection apiAvailable={available} />
    </div>
  );
};

export default AccountSettings;

/* ─── Profile ─── */

const ProfileSection = ({ apiAvailable, apiUserData }) => {
  const { user, session, updateSession } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    username: user?.username || '',
    email: user?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (apiUserData) {
      setUserId(apiUserData.pk || apiUserData.id);
      setForm({
        name: apiUserData.name || '',
        username: apiUserData.username || '',
        email: apiUserData.email || '',
      });
    }
  }, [apiUserData]);

  const handleSave = async () => {
    if (!userId) {
      setStatus({ type: 'error', message: 'Cannot save — identity server is unavailable' });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      await updateUser(userId, { name: form.name, username: form.username });
      const updatedSession = {
        ...session,
        user: { ...session.user, name: form.name, username: form.username },
      };
      localStorage.setItem('forge_session', JSON.stringify(updatedSession));
      updateSession(updatedSession);
      setStatus({ type: 'success', message: 'Profile updated. Changes will sync to Forums on next login.' });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    }
    setSaving(false);
  };

  return (
    <div className="glass-frosted rounded-2xl p-6 glow-subtle">
      <h2 className="text-lg font-semibold text-foreground-primary mb-6">Profile Information</h2>

      {/* Avatar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          {user?.avatar ? (
            <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold" style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}>
              {(user?.name || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <div
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}
            title="Avatar is managed by your identity provider"
          >
            <Camera size={14} />
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-foreground-primary">{user?.name}</p>
          <p className="text-xs text-foreground-muted">Avatar is managed by your identity provider</p>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-4">
        <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} disabled={apiAvailable === false} />
        <Field label="Username" value={form.username} onChange={(v) => setForm({ ...form, username: v })} disabled={apiAvailable === false} />
        <Field label="Email" value={form.email} disabled hint="Email is managed by your identity provider" />
      </div>

      {status && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${status.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
          {status.message}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving || apiAvailable === false}
        className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-smooth"
        style={{ background: '#F97316', color: '#0B0E13', opacity: (saving || apiAvailable === false) ? 0.6 : 1 }}
      >
        <Save size={16} />
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

/* ─── Password ─── */

const PasswordSection = ({ apiAvailable, apiUserData }) => {
  const [form, setForm] = useState({ newPass: '', confirm: '' });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const userId = apiUserData?.pk || apiUserData?.id || null;

  const handleChangePassword = async () => {
    if (form.newPass !== form.confirm) {
      setStatus({ type: 'error', message: 'Passwords do not match' });
      return;
    }
    if (form.newPass.length < 8) {
      setStatus({ type: 'error', message: 'Password must be at least 8 characters' });
      return;
    }
    if (!userId) return;
    setSaving(true);
    setStatus(null);
    try {
      await setPassword(userId, form.newPass);
      setForm({ newPass: '', confirm: '' });
      setStatus({ type: 'success', message: 'Password changed successfully' });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    }
    setSaving(false);
  };

  const disabled = apiAvailable === false;

  return (
    <div className="glass-frosted rounded-2xl p-6 glow-subtle">
      <h2 className="text-lg font-semibold text-foreground-primary mb-6">Change Password</h2>
      {disabled ? (
        <p className="text-sm text-foreground-muted">Password change is unavailable — unable to reach the identity server.</p>
      ) : (
        <>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm text-foreground-secondary mb-1.5">New Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.newPass}
                  onChange={(e) => setForm({ ...form, newPass: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg text-sm bg-subtle text-foreground-primary border border-subtle focus:outline-none focus:border-orange-500/50 transition-smooth"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground-primary transition-smooth"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-foreground-secondary mb-1.5">Confirm New Password</label>
              <input
                type={showPass ? 'text' : 'password'}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg text-sm bg-subtle text-foreground-primary border border-subtle focus:outline-none focus:border-orange-500/50 transition-smooth"
                placeholder="Confirm new password"
              />
            </div>
          </div>

          {status && (
            <div className={`mt-4 p-3 rounded-lg text-sm ${status.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
              {status.message}
            </div>
          )}

          <button
            onClick={handleChangePassword}
            disabled={saving || !form.newPass || !form.confirm}
            className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-smooth"
            style={{ background: '#F97316', color: '#0B0E13', opacity: (saving || !form.newPass) ? 0.6 : 1 }}
          >
            {saving ? 'Changing...' : 'Change Password'}
          </button>
        </>
      )}
    </div>
  );
};

/* ─── 2FA ─── */

const TwoFactorSection = ({ apiAvailable }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (apiAvailable === false) {
      setLoading(false);
      return;
    }
    setLoading(true);
    listTotpDevices()
      .then((data) => setDevices(data.results || []))
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, [apiAvailable]);

  useEffect(() => { load(); }, [load]);

  const handleRemove = async (id) => {
    try {
      await deleteTotpDevice(id);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const disabled = apiAvailable === false;

  return (
    <div className="glass-frosted rounded-2xl p-6 glow-subtle">
      <h2 className="text-lg font-semibold text-foreground-primary mb-2">Two-Factor Authentication</h2>
      <p className="text-sm text-foreground-secondary mb-6">Add an extra layer of security to your account</p>

      {disabled ? (
        <p className="text-sm text-foreground-muted">2FA management is unavailable — unable to reach the identity server.</p>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-muted">
          <RefreshCw size={14} className="animate-spin" /> Loading...
        </div>
      ) : devices.length === 0 ? (
        <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
          <AlertTriangle size={18} style={{ color: '#F97316', marginTop: '2px' }} />
          <div>
            <p className="text-sm font-medium text-foreground-primary">2FA is not enabled</p>
            <p className="text-xs text-foreground-secondary mt-1">We strongly recommend enabling two-factor authentication. Contact your administrator to set up 2FA.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {devices.map((d) => (
            <div key={d.pk} className="flex items-center justify-between p-3 rounded-lg bg-subtle">
              <div className="flex items-center gap-3">
                <Shield size={16} style={{ color: '#22c55e' }} />
                <div>
                  <p className="text-sm font-medium text-foreground-primary">{d.name || 'TOTP Authenticator'}</p>
                  <p className="text-xs text-foreground-muted">Added {new Date(d.created).toLocaleDateString()}</p>
                </div>
              </div>
              <button onClick={() => handleRemove(d.pk)} className="text-foreground-muted hover:text-red-400 transition-smooth" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Sessions ─── */

const APP_NAMES = {
  'forge-studio': { name: 'Forge Studio Desktop', icon: Monitor, color: '#F97316' },
  'forge-website': { name: 'Forge Website', icon: Globe, color: '#3b82f6' },
  'forums': { name: 'Forums', icon: MessageSquare, color: '#a855f7' },
};

const getAppInfo = (token) => {
  // Match by provider name or app slug
  const providerName = (token.provider?.name || token.provider?.verbose_name || '').toLowerCase();
  const appSlug = (token.provider?.application?.slug || '').toLowerCase();
  const combined = `${providerName} ${appSlug}`;

  if (combined.includes('studio') || combined.includes('forge-studio') || combined.includes('desktop'))
    return APP_NAMES['forge-studio'];
  if (combined.includes('forum') || combined.includes('discourse'))
    return APP_NAMES['forums'];
  if (combined.includes('forge') || combined.includes('website'))
    return APP_NAMES['forge-website'];

  return { name: providerName || 'Unknown App', icon: AppWindow, color: '#6b7280' };
};

const OS_ICONS = { Linux: Monitor, Darwin: Laptop, Windows: Monitor };

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const isStale = (iso) => {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > 7 * 24 * 60 * 60 * 1000;
};

const SessionsSection = ({ apiAvailable }) => {
  const [authSessions, setAuthSessions] = useState([]);
  const [appTokens, setAppTokens] = useState([]);
  const [desktopDevices, setDesktopDevices] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (apiAvailable === false) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listSessions().then((data) => data.results || []).catch(() => []),
      listRefreshTokens().then((data) => data.results || []).catch(() => []),
      getMe().then((data) => (data.user || data)?.attributes?.forge_devices || {}).catch(() => ({})),
    ]).then(([sessions, tokens, devices]) => {
      setAuthSessions(sessions);
      setAppTokens(tokens);
      setDesktopDevices(devices);
    }).finally(() => setLoading(false));
  }, [apiAvailable]);

  useEffect(() => { load(); }, [load]);

  const handleRevokeSession = async (uuid) => {
    try {
      await revokeSession(uuid);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRevokeToken = async (id) => {
    try {
      await revokeRefreshToken(id);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const disabled = apiAvailable === false;

  // Build a combined view: app tokens enriched with session IP data
  const sessionIpMap = {};
  authSessions.forEach((s) => {
    if (s.last_ip) sessionIpMap[s.last_ip] = s;
  });

  return (
    <div className="glass-frosted rounded-2xl p-6 glow-subtle">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground-primary">Active Sessions</h2>
          <p className="text-sm text-foreground-secondary mt-1">Apps and devices signed into your account</p>
        </div>
        {!disabled && (
          <button onClick={load} className="text-foreground-muted hover:text-foreground-primary transition-smooth" title="Refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      {disabled ? (
        <p className="text-sm text-foreground-muted">Session management is unavailable — unable to reach the identity server.</p>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-muted">
          <RefreshCw size={14} className="animate-spin" /> Loading sessions...
        </div>
      ) : appTokens.length === 0 && authSessions.length === 0 && Object.keys(desktopDevices).length === 0 ? (
        <p className="text-sm text-foreground-muted">No active sessions found.</p>
      ) : (
        <div className="space-y-3">
          {/* Desktop devices (from forge_devices attribute) */}
          {Object.entries(desktopDevices).map(([deviceId, d]) => {
            const DevIcon = OS_ICONS[d.os] || Monitor;
            const stale = isStale(d.last_seen);
            return (
              <div key={deviceId} className="flex items-center justify-between p-4 rounded-xl bg-subtle">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: stale ? 'rgba(107,114,128,0.1)' : 'rgba(249,115,22,0.1)' }}>
                    <DevIcon size={20} style={{ color: stale ? '#6b7280' : '#F97316' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground-primary">{d.hostname || deviceId}</p>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: stale ? 'rgba(107,114,128,0.1)' : 'rgba(249,115,22,0.1)', color: stale ? '#6b7280' : '#F97316' }}
                      >
                        {d.os || 'Desktop'}
                      </span>
                      {stale && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'rgba(234,179,8,0.1)', color: '#eab308' }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                      <span className="text-xs text-foreground-muted">{d.app} v{d.app_version}</span>
                      {d.public_ip && d.public_ip !== 'unknown' && (
                        <span className="text-xs text-foreground-muted">IP: {d.public_ip}</span>
                      )}
                      {d.last_seen && (
                        <span className="text-xs text-foreground-muted flex items-center gap-1">
                          <Clock size={10} /> {timeAgo(d.last_seen)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* App sessions (from refresh tokens) */}
          {appTokens.map((t) => {
            const app = getAppInfo(t);
            const Icon = app.icon;
            const lastUsed = t.updated || t.last_used;
            return (
              <div key={t.id} className="flex items-center justify-between p-4 rounded-xl bg-subtle">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${app.color}15` }}>
                    <Icon size={20} style={{ color: app.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground-primary">{app.name}</p>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: `${app.color}15`, color: app.color }}
                      >
                        App
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                      {t.id_token && (
                        <span className="text-xs text-foreground-muted">
                          Scope: {t.scope?.join(', ') || 'standard'}
                        </span>
                      )}
                      {lastUsed && (
                        <span className="text-xs text-foreground-muted">
                          Last used {new Date(lastUsed).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleRevokeToken(t.id)}
                  className="text-xs text-foreground-muted hover:text-red-400 transition-smooth px-3 py-1.5 rounded-lg hover:bg-subtle flex-shrink-0"
                  title="Revoke this app's access"
                >
                  Revoke
                </button>
              </div>
            );
          })}

          {/* Browser sessions (from authenticated_sessions) */}
          {authSessions.map((s) => (
            <div key={s.uuid} className="flex items-center justify-between p-4 rounded-xl bg-subtle">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-subtle">
                  <Globe size={20} className="text-foreground-muted" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground-primary">Browser Session</p>
                    {s.current && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                        style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                    {s.last_ip && (
                      <span className="text-xs text-foreground-muted">IP: {s.last_ip}</span>
                    )}
                    {s.last_used && (
                      <span className="text-xs text-foreground-muted">
                        Last used {new Date(s.last_used).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {!s.current && (
                <button
                  onClick={() => handleRevokeSession(s.uuid)}
                  className="text-xs text-foreground-muted hover:text-red-400 transition-smooth px-3 py-1.5 rounded-lg hover:bg-subtle flex-shrink-0"
                  title="Sign out this session"
                >
                  Sign Out
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Shared ─── */

const Field = ({ label, value, onChange, disabled, hint, type = 'text' }) => (
  <div>
    <label className="block text-sm text-foreground-secondary mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      disabled={disabled}
      className={`w-full px-4 py-2.5 rounded-lg text-sm bg-subtle text-foreground-primary border border-subtle focus:outline-none focus:border-orange-500/50 transition-smooth ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    />
    {hint && <p className="text-xs text-foreground-muted mt-1">{hint}</p>}
  </div>
);
